import type { QueryStatus } from '@tanstack/react-query';

import type { ChartType } from '@revelio/core';
import { getOtherColor, getPrimaryColor } from '@revelio/replots';

import { cleanGeoLabel } from '../../../utils/clean-geo-label';
import type { LocationType } from '../../Chart/Map/types';
import {
  type BoxDataSet,
  type BoxSeriesDataSet,
  type BreakdownBoxDataSet,
  type BreakdownSingleDataSet,
  DataProviderQueryStatus,
  type DataProviderSet,
  DataType,
  type DistributionDataSet,
  type MapDataSet,
  type SeriesType,
  type SingleDataSet,
  type TableRowsDataSet,
  type TextDataSet,
  type TimeseriesDataSet,
} from '../types';

type Row = Record<string, unknown>;

function asString(val: unknown): string {
  if (typeof val === 'string') return val;
  if (val === null || val === undefined) return '';
  return String(val);
}

function asNumberOrNull(val: unknown): number | null {
  if (val === null || val === undefined) return null;
  const n = Number(val);
  return Number.isNaN(n) ? null : n;
}

function asNumber(val: unknown): number {
  return asNumberOrNull(val) ?? 0;
}

function asOptionalString(val: unknown): string | null {
  if (val === null || val === undefined) return null;
  return String(val);
}

function hasSegmentColumn(rows: Row[]): boolean {
  if (rows.length === 0) return false;
  return rows[0] !== undefined && 'SEGMENT' in rows[0];
}

const SERIES_TYPE_VALUES: SeriesType[] = ['primary', 'benchmark', 'other'];
const BENCHMARK_COLOR = '#93C5FD';

function getSeriesType(row: Row): SeriesType | undefined {
  const raw =
    row.SERIES_TYPE !== undefined
      ? row.SERIES_TYPE
      : (row as Record<string, unknown>).series_type;
  if (raw === undefined || raw === null) return undefined;
  const s = String(raw).trim().toLowerCase();
  return SERIES_TYPE_VALUES.includes(s as SeriesType)
    ? (s as SeriesType)
    : undefined;
}

function getSeriesTypeColor(seriesType: SeriesType): string {
  if (seriesType === 'primary') return getPrimaryColor();
  if (seriesType === 'benchmark') return BENCHMARK_COLOR;
  return getOtherColor();
}

function transformToSingle(rows: Row[], defaultColor?: string): SingleDataSet {
  return {
    type: DataType.SINGLE,
    data: rows.map((row) => {
      const seriesType = getSeriesType(row);
      return {
        dim: cleanGeoLabel(asString(row.UNIT_OF_ANALYSIS)),
        value: asNumberOrNull(row.VALUE),
        ...(row.SHARE !== undefined
          ? { share: asNumberOrNull(row.SHARE) }
          : {}),
        ...(row.ID !== undefined ? { id: asOptionalString(row.ID) } : {}),
        ...(row.LONG_NAME !== undefined
          ? { longName: asOptionalString(row.LONG_NAME) }
          : {}),
        ...(seriesType !== undefined
          ? { seriesType, color: getSeriesTypeColor(seriesType) }
          : {}),
        ...(seriesType === undefined && defaultColor !== undefined
          ? { color: defaultColor }
          : {}),
      };
    }),
  };
}

function transformToTimeseries(rows: Row[]): TimeseriesDataSet {
  const grouped = new Map<
    string,
    {
      id?: string | null;
      dim: string;
      timeseries: TimeseriesDataSet['data'][number]['timeseries'];
    }
  >();

  for (const row of rows) {
    const dim = cleanGeoLabel(asString(row.UNIT_OF_ANALYSIS));
    let entry = grouped.get(dim);
    if (!entry) {
      entry = {
        dim,
        timeseries: [],
        ...(row.ID !== undefined ? { id: asOptionalString(row.ID) } : {}),
      };
      grouped.set(dim, entry);
    }
    entry.timeseries.push({
      date: asString(row.DATE),
      value: asNumberOrNull(row.VALUE),
      ...(row.SHARE !== undefined ? { share: asNumberOrNull(row.SHARE) } : {}),
    });
  }

  return {
    type: DataType.TIMESERIES,
    data: Array.from(grouped.values()),
  };
}

function transformToBreakdownSingle(rows: Row[]): BreakdownSingleDataSet {
  const grouped = new Map<string, BreakdownSingleDataSet['data'][number]>();

  for (const row of rows) {
    const groupKey = cleanGeoLabel(asString(row.UNIT_OF_ANALYSIS));
    let entry = grouped.get(groupKey);
    if (!entry) {
      entry = { dim: groupKey, breakdown: [] };
      grouped.set(groupKey, entry);
    }
    entry.breakdown.push({
      dim: cleanGeoLabel(asString(row.SEGMENT)),
      value: asNumberOrNull(row.VALUE),
      ...(row.SHARE !== undefined ? { share: asNumberOrNull(row.SHARE) } : {}),
    });
  }

  return {
    type: DataType.BREAKDOWN_SINGLE,
    data: Array.from(grouped.values()),
  };
}

function transformToBreakdownTimeseries(rows: Row[]): DataProviderSet {
  const grouped = new Map<
    string,
    {
      dim: string;
      breakdown: {
        date: string;
        value: number | null;
        share?: number | null;
      }[];
    }
  >();

  for (const row of rows) {
    const segmentKey = cleanGeoLabel(asString(row.SEGMENT));
    let entry = grouped.get(segmentKey);
    if (!entry) {
      entry = { dim: segmentKey, breakdown: [] };
      grouped.set(segmentKey, entry);
    }
    entry.breakdown.push({
      date: asString(row.DATE),
      value: asNumberOrNull(row.VALUE),
      ...(row.SHARE !== undefined ? { share: asNumberOrNull(row.SHARE) } : {}),
    });
  }

  return {
    type: DataType.BREAKDOWN_TIMESERIES,
    data: Array.from(grouped.values()),
  };
}

function transformToBox(rows: Row[]): BoxSeriesDataSet {
  return {
    type: DataType.BOX,
    data: rows.map(
      (row): BoxDataSet => ({
        dim: cleanGeoLabel(asString(row.UNIT_OF_ANALYSIS)),
        box: {
          q10: asNumber(row.Q10),
          q50: asNumber(row.Q50),
          q90: asNumber(row.Q90),
        },
        ...(row.ID !== undefined ? { id: asOptionalString(row.ID) } : {}),
      })
    ),
  };
}

function transformToBreakdownBox(rows: Row[]): BreakdownBoxDataSet {
  const grouped = new Map<string, BreakdownBoxDataSet['data'][number]>();

  for (const row of rows) {
    const groupKey = cleanGeoLabel(asString(row.UNIT_OF_ANALYSIS));
    let entry = grouped.get(groupKey);
    if (!entry) {
      entry = { dim: groupKey, breakdown: [] };
      grouped.set(groupKey, entry);
    }
    entry.breakdown.push({
      dim: cleanGeoLabel(asString(row.SEGMENT)),
      box: {
        q10: asNumber(row.Q10),
        q50: asNumber(row.Q50),
        q90: asNumber(row.Q90),
      },
    });
  }

  return {
    type: DataType.BOX,
    data: Array.from(grouped.values()),
  };
}

function transformToDistribution(rows: Row[]): DistributionDataSet {
  const grouped = new Map<string, DistributionDataSet['data'][number]>();

  for (const row of rows) {
    const dim = cleanGeoLabel(asString(row.UNIT_OF_ANALYSIS));
    let entry = grouped.get(dim);
    if (!entry) {
      entry = {
        dim,
        distribution: [],
        ...(row.ID !== undefined ? { id: asOptionalString(row.ID) } : {}),
      };
      grouped.set(dim, entry);
    }
    entry.distribution.push({
      value: asNumber(row.VALUE),
      kde: asNumber(row.KDE),
      cdf: asNumber(row.CDF),
    });
  }

  return {
    type: DataType.DISTRIBUTION,
    data: Array.from(grouped.values()),
  };
}

function transformToMap(rows: Row[]): MapDataSet {
  return {
    type: DataType.MAP,
    points: rows.map((row) => ({
      id: asString(row.ID),
      label: asString(row.LABEL),
      lat: asNumber(row.LAT),
      lon: asNumber(row.LON),
      value: asNumber(row.VALUE),
      locationType: asString(row.LOCATION_TYPE) as LocationType,
    })),
  };
}

function transformToTableRows(rows: Row[]): TableRowsDataSet {
  return {
    type: DataType.TABLE_ROWS,
    data: rows,
  };
}

function transformToText(rows: Row[]): TextDataSet {
  return {
    type: DataType.TEXT,
    data: rows.map((row) => ({
      entity: asString(row.ENTITY),
      text: asString(row.TEXT),
      ...(row.ID !== undefined ? { id: asOptionalString(row.ID) } : {}),
    })),
  };
}

type SnowflakeChartType = Extract<
  ChartType,
  | 'bar'
  | 'metric'
  | 'treemap'
  | 'diverging-bar'
  | 'diverging-bar-combo'
  | 'line'
  | 'volume'
  | 'stacked-bar'
  | 'grouped-bar'
  | 'sankey'
  | 'chord'
  | 'box'
  | 'distribution'
  | 'map'
  | 'table'
  | 'text'
  | 'gauge-v2'
>;

function resolveDataType(chartType: ChartType, rows: Row[]): DataType {
  const segment = hasSegmentColumn(rows);

  const mapping: Record<SnowflakeChartType, DataType> = {
    'gauge-v2': DataType.SINGLE,
    metric: DataType.SINGLE,
    bar: DataType.SINGLE,
    treemap: segment ? DataType.BREAKDOWN_SINGLE : DataType.SINGLE,
    'diverging-bar': segment ? DataType.BREAKDOWN_SINGLE : DataType.SINGLE,
    'diverging-bar-combo': DataType.BREAKDOWN_SINGLE,
    line: DataType.TIMESERIES,
    volume: DataType.TIMESERIES,
    'stacked-bar': segment ? DataType.BREAKDOWN_SINGLE : DataType.SINGLE,
    'grouped-bar': segment ? DataType.BREAKDOWN_SINGLE : DataType.SINGLE,
    sankey: DataType.BREAKDOWN_SINGLE,
    chord: DataType.BREAKDOWN_SINGLE,
    box: DataType.BOX,
    distribution: DataType.DISTRIBUTION,
    map: DataType.MAP,
    table: DataType.TABLE_ROWS,
    text: DataType.TEXT,
  };

  return mapping[chartType as SnowflakeChartType] ?? DataType.TABLE_ROWS;
}

type ValidationResult = {
  isValid: boolean;
  warningMessage?: string;
};

function validateRowsForDataType(
  rows: Row[],
  dataType: DataType,
  chartType: ChartType
): ValidationResult {
  if (rows.length === 0) {
    return { isValid: true };
  }

  const baseContext = `Snowflake data shape for chartType "${chartType}" and dataType "${dataType}"`;

  const makeMessage = (details: string): string => {
    return `${baseContext} may not match the expected format: ${details}`;
  };

  switch (dataType) {
    case DataType.SINGLE: {
      const hasUnitOfAnalysis = rows.some(
        (row) => row.UNIT_OF_ANALYSIS !== undefined
      );
      const hasValue = rows.some((row) => row.VALUE !== undefined);

      if (!hasUnitOfAnalysis && !hasValue) {
        return {
          isValid: false,
          warningMessage: makeMessage(
            'expected at least one of "UNIT_OF_ANALYSIS" or "VALUE" columns.'
          ),
        };
      }

      const warnings: string[] = [];

      if (!hasUnitOfAnalysis) {
        warnings.push(
          'missing "UNIT_OF_ANALYSIS" column; using empty string for dimension.'
        );
      }

      if (!hasValue) {
        warnings.push('missing "VALUE" column; using null for numeric values.');
      }

      if (warnings.length === 0) {
        return { isValid: true };
      }

      return {
        isValid: true,
        warningMessage: makeMessage(warnings.join(' ')),
      };
    }
    case DataType.TIMESERIES: {
      const hasDate = rows.some((row) => row.DATE !== undefined);
      const hasValue = rows.some((row) => row.VALUE !== undefined);

      if (!hasDate || !hasValue) {
        return {
          isValid: false,
          warningMessage: makeMessage(
            'expected "DATE" and "VALUE" columns for timeseries data.'
          ),
        };
      }

      return { isValid: true };
    }
    case DataType.BREAKDOWN_SINGLE: {
      const hasUnitOfAnalysis = rows.some(
        (row) => row.UNIT_OF_ANALYSIS !== undefined
      );
      const hasSegment = rows.some((row) => row.SEGMENT !== undefined);
      const hasValue = rows.some((row) => row.VALUE !== undefined);

      if (!hasUnitOfAnalysis || !hasSegment || !hasValue) {
        return {
          isValid: false,
          warningMessage: makeMessage(
            'expected "UNIT_OF_ANALYSIS", "SEGMENT", and "VALUE" columns for breakdown data.'
          ),
        };
      }

      return { isValid: true };
    }
    case DataType.BREAKDOWN_TIMESERIES: {
      const hasSegment = rows.some((row) => row.SEGMENT !== undefined);
      const hasDate = rows.some((row) => row.DATE !== undefined);
      const hasValue = rows.some((row) => row.VALUE !== undefined);

      if (!hasSegment || !hasDate || !hasValue) {
        return {
          isValid: false,
          warningMessage: makeMessage(
            'expected "SEGMENT", "DATE", and "VALUE" columns for breakdown timeseries data.'
          ),
        };
      }

      return { isValid: true };
    }
    case DataType.BOX: {
      if (hasSegmentColumn(rows)) {
        const hasUnitOfAnalysis = rows.some(
          (row) => row.UNIT_OF_ANALYSIS !== undefined
        );
        const hasSegment = rows.some((row) => row.SEGMENT !== undefined);
        const hasQ10 = rows.some((row) => row.Q10 !== undefined);
        const hasQ50 = rows.some((row) => row.Q50 !== undefined);
        const hasQ90 = rows.some((row) => row.Q90 !== undefined);

        if (
          !hasUnitOfAnalysis ||
          !hasSegment ||
          !hasQ10 ||
          !hasQ50 ||
          !hasQ90
        ) {
          return {
            isValid: false,
            warningMessage: makeMessage(
              'expected "UNIT_OF_ANALYSIS", "SEGMENT", "Q10", "Q50", and "Q90" columns for breakdown box data.'
            ),
          };
        }

        return { isValid: true };
      }

      const hasQ10 = rows.some((row) => row.Q10 !== undefined);
      const hasQ50 = rows.some((row) => row.Q50 !== undefined);
      const hasQ90 = rows.some((row) => row.Q90 !== undefined);

      if (!hasQ10 || !hasQ50 || !hasQ90) {
        return {
          isValid: false,
          warningMessage: makeMessage(
            'expected "Q10", "Q50", and "Q90" columns for box plot data.'
          ),
        };
      }

      return { isValid: true };
    }
    case DataType.DISTRIBUTION: {
      const hasValue = rows.some((row) => row.VALUE !== undefined);
      const hasKde = rows.some((row) => row.KDE !== undefined);
      const hasCdf = rows.some((row) => row.CDF !== undefined);

      if (!hasValue || !hasKde || !hasCdf) {
        return {
          isValid: false,
          warningMessage: makeMessage(
            'expected "VALUE", "KDE", and "CDF" columns for distribution data.'
          ),
        };
      }

      return { isValid: true };
    }
    case DataType.MAP: {
      const hasId = rows.some((row) => row.ID !== undefined);
      const hasLabel = rows.some((row) => row.LABEL !== undefined);
      const hasLat = rows.some((row) => row.LAT !== undefined);
      const hasLon = rows.some((row) => row.LON !== undefined);
      const hasValue = rows.some((row) => row.VALUE !== undefined);
      const hasLocationType = rows.some(
        (row) => row.LOCATION_TYPE !== undefined
      );

      if (
        !hasId ||
        !hasLabel ||
        !hasLat ||
        !hasLon ||
        !hasValue ||
        !hasLocationType
      ) {
        return {
          isValid: false,
          warningMessage: makeMessage(
            'expected "ID", "LABEL", "LAT", "LON", "VALUE", and "LOCATION_TYPE" columns for map data.'
          ),
        };
      }

      return { isValid: true };
    }
    case DataType.TEXT: {
      const hasEntity = rows.some((row) => row.ENTITY !== undefined);
      const hasText = rows.some((row) => row.TEXT !== undefined);

      if (!hasEntity || !hasText) {
        return {
          isValid: false,
          warningMessage: makeMessage(
            'expected "ENTITY" and "TEXT" columns for text data.'
          ),
        };
      }

      return { isValid: true };
    }
    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case DataType.TABLE_ROWS:
    default: {
      return { isValid: true };
    }
  }
}

function transformByDataType(
  rows: Row[],
  dataType: DataType,
  defaultColor?: string
): DataProviderSet {
  switch (dataType) {
    case DataType.SINGLE:
      return transformToSingle(rows, defaultColor);
    case DataType.TIMESERIES:
      return transformToTimeseries(rows);
    case DataType.BREAKDOWN_SINGLE:
      return transformToBreakdownSingle(rows);
    case DataType.BREAKDOWN_TIMESERIES:
      return transformToBreakdownTimeseries(rows);
    case DataType.BOX:
      return hasSegmentColumn(rows)
        ? transformToBreakdownBox(rows)
        : transformToBox(rows);
    case DataType.DISTRIBUTION:
      return transformToDistribution(rows);
    case DataType.MAP:
      return transformToMap(rows);
    case DataType.TEXT:
      return transformToText(rows);
    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case DataType.TABLE_ROWS:
    default:
      return transformToTableRows(rows);
  }
}

export function transformSnowflakeRows({
  rows,
  chartType,
  status,
  enabled,
  defaultColor,
}: {
  rows: Row[];
  chartType: ChartType;
  status: QueryStatus;
  enabled: boolean;
  defaultColor?: string;
}): {
  dataSet: DataProviderSet;
  dataStatus: DataProviderQueryStatus;
} {
  const dataType = resolveDataType(chartType, rows);
  const validation = validateRowsForDataType(rows, dataType, chartType);

  let dataStatus = (() => {
    if (!enabled) return DataProviderQueryStatus.Success;
    if (status === 'error') return DataProviderQueryStatus.Error;
    if (status === 'pending') return DataProviderQueryStatus.Fetching;
    if (status === 'success') return DataProviderQueryStatus.Success;
    return DataProviderQueryStatus.Idle;
  })();

  if (!validation.isValid) {
    const message =
      validation.warningMessage ??
      `Snowflake data shape for chartType "${chartType}" and dataType "${dataType}" is invalid.`;
    console.warn(message, { chartType, dataType });
    dataStatus = DataProviderQueryStatus.Error;
  }

  if (validation.warningMessage !== undefined) {
    console.warn(validation.warningMessage, { chartType, dataType });
  }

  return {
    dataSet: transformByDataType(rows, dataType, defaultColor),
    dataStatus,
  };
}
